import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import Header from './components/Header';
import Footer from './components/Footer';
import RegistrationForm from './components/RegistrationForm';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import ManageMembers from './components/ManageMembers';
import AdminEditUserForm from './components/AdminEditUserForm';
import BulkImportPage from './components/BulkImportPage';
import StatisticsDashboard from './components/StatisticsDashboard';
import ScanAnalyticsDashboard from './components/ScanAnalyticsDashboard';
import AdminContactMessages from './components/AdminContactMessages';
import AdminReviews from './components/AdminReviews';
import NotFound from './components/NotFound';
import ProfilePage from './components/ProfilePage';
import UserDashboard from './components/UserDashboard';
import ChangePasswordForm from './components/ChangePasswordForm';
import EditProfileForm from './components/EditProfileForm';
import AdminAddUserForm from './components/AdminAddUserForm';
import CheckoutPage from './components/CheckoutPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminPricingMenu from './components/AdminPricingMenu';

import LandingPage from './components/LandingPage';

import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import ContactUs from './components/ContactUs';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) { return <Navigate to="/login" />; }
  return children;
};

// Updated Layout to center content
const MainLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
      {children}
    </main>
    <Footer />
  </div>
);

const AdminLayout = ({ children }) => (
  <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    {children}
  </div>
);

// Layout for Public Profile (No Header)
const PublicProfileLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
      {children}
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <div className="bg-gradient-mesh text-brand-secondary font-sans min-h-screen">
      <Router>
        <Routes>
          {/* Public Routes with Main Layout */}
          <Route path="/" element={<MainLayout><LandingPage /></MainLayout>} />
          <Route path="/register" element={<MainLayout><RegistrationForm /></MainLayout>} />
          <Route path="/login" element={<MainLayout><LoginForm /></MainLayout>} />
          <Route path="/forgot-password" element={<MainLayout><ForgotPassword /></MainLayout>} />
          <Route path="/reset-password" element={<MainLayout><ResetPassword /></MainLayout>} />
          <Route path="/member/:memberId" element={<PublicProfileLayout><ProfilePage /></PublicProfileLayout>} />

          <Route path="/terms-and-conditions" element={<MainLayout><TermsAndConditions /></MainLayout>} />
          <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
          <Route path="/cancellation-refund-policy" element={<MainLayout><RefundPolicy /></MainLayout>} />
          <Route path="/contact-us" element={<MainLayout><ContactUs /></MainLayout>} />

          {/* Protected User Routes with Main Layout */}
          <Route path="/my-dashboard" element={<ProtectedRoute><MainLayout><UserDashboard /></MainLayout></ProtectedRoute>} />
          <Route path="/edit-profile" element={<ProtectedRoute><MainLayout><EditProfileForm /></MainLayout></ProtectedRoute>} />
          {/* NOTE: Member Directory intentionally removed */}
          <Route path="/change-password" element={<ProtectedRoute><MainLayout><ChangePasswordForm /></MainLayout></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><MainLayout><CheckoutPage /></MainLayout></ProtectedRoute>} />
          {/* Protected Admin Routes with their own layout */}
          <Route
            path="/portal-management-7a8b9c"
            element={<ProtectedRoute><AdminLayout><Dashboard /></AdminLayout></ProtectedRoute>}
          >
            <Route index element={<Navigate to="stats" replace />} />
            <Route path="stats" element={<StatisticsDashboard />} />
            <Route path="manage-members" element={<ManageMembers />} />
            <Route path="edit-user/:userId" element={<AdminEditUserForm />} />
            <Route path="add-member" element={<AdminAddUserForm />} />
            <Route path="bulk-import" element={<BulkImportPage />} />
            <Route path="analytics" element={<ScanAnalyticsDashboard />} />
            <Route path="messages" element={<AdminContactMessages />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="pricing" element={<AdminPricingMenu />} />
          </Route>

          {/* Catch-all for unknown routes */}
          <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;

